<template>
  <div class="toll-upload-component">
    <p class="alert alert-warning">
      <span class="fa fa-fw fa-sort-amount-up"></span> When sorting columns, individual line items will be grouped by Car #
    </p>
    <div class="row">
      <div class="col-sm-9">
        <responsive-table :table="reservationsTable" @orderColumn="orderReservations($event)" default-sort-column="reservationId" default-sort="ASC">
          <template v-for="(charge, i) in charges" slot="rows">
            <tr :key="i" v-if="i === 0 || charge.reservation.AccountUuid != charges[i-1].reservation.AccountUuid" class="bg-primary">
              <td colspan="6">
                <strong class="text-white">
                  <span class="fa fa-map fa-fw"></span> {{ charge.reservation.Lot.suburb }}, {{ charge.reservation.Lot.postcode }}
                </strong>
              </td>
            </tr>
            <tr :key="i">
              <td>
                <router-link v-if="charge.reservation" :to="{ path: `/fleet/reservations/${charge.reservation.uuid}` }">
                  #{{ charge.reservation.reservationId }}
                </router-link>
              </td>
              <td>{{ charge.toll.description }}</td>
              <td>{{ charge.toll.startTime | date_format }}</td>
              <td>{{ charge.toll.endTime | date_format }}</td>
              <td>{{ charge.toll.cost | to_dollar_amount }}</td>
              <td>{{ charge.toll.toBeCharged | to_dollar_amount }}</td>
            </tr>
          </template>
        </responsive-table>
        <template v-if="charges.length === 0">
          <div class="alert alert-info">
            <span class="fa-stack fa-fw">
              <span class="fa fa-dollar-sign fa-stack-1x"></span>
              <span class="fa fa-ban fa-stack-2x"></span>
            </span>
            No Reservations To Charge
          </div>
        </template>
        <hr class="dark"/>
        <responsive-table :table="chargesTable"  @orderColumn="orderCharges($event)" default-sort-column="convadisId" default-sort="ASC">
          <template v-for="car in nonCharges" slot="rows">
            <tr :key="car.uuid" class="bg-primary">
              <td colspan="6">
                <strong class="text-white">
                  <span class="fa fa-car fa-fw"></span> Car #{{ car[0].toll.carId }} - {{ car[0].toll.rego }} Tolls Not Assigned to User
                </strong>
              </td>
            </tr>
            <template v-for="(charge, i) in car">
              <tr :key="i">
                <td>#{{ charge.toll.carId }} - {{ charge.toll.rego }}</td>
                <td>{{ charge.toll.description }}</td>
                <td>{{ charge.toll.startTime | date_format }}</td>
                <td>{{ charge.toll.endTime | date_format }}</td>
                <td>{{ charge.toll.cost | to_dollar_amount }}</td>
                <td>{{ charge.toll.toBeCharged | to_dollar_amount }}</td>
              </tr>
            </template>
          </template>
        </responsive-table>
        <template v-if="nonCharges.length === 0">
          <div class="alert alert-info">
            <span class="fa-stack fa-fw">
              <span class="fa fa-dollar-sign fa-stack-1x"></span>
              <span class="fa fa-ban fa-stack-2x"></span>
            </span>
            No Charges To Invoice
          </div>
        </template>
      </div>
      <div class="col-sm-3">
        <div class="card mb-2 card-primary">
          <div class="card-header">
            <i class="fa fa-fw fa-upload"></i> Upload Toll File
          </div>
          <div class="card-body">
            <div ref="dropzone">
              <input type="hidden" name="tollFile" id="tollFile"/>
              Choose File(s)
            </div>
            <div class="progress mt-2" v-show="isUploading">
              <div class="progress-bar progress-bar-success" role="progressbar" v-bind:style="{ width: progress + '%' }">
                {{ progressMessage }}
              </div>
            </div>
          </div>
        </div>
        <div class="card mb-2 card-primary">
          <div class="card-header">
            <span class="fa fa-dollar-sign"></span> To Be Charged
          </div>
          <div class="card-body p-0">
            <table class="table table-bordered table-striped table-hover table-sm mb-0">
              <tr>
                <td><strong>Total Cost</strong></td>
                <td>{{ chargesBreakdown.cost | to_dollar_amount }}</td>
              </tr>
              <tr>
                <td><strong>Total to be Charged</strong></td>
                <td>{{ chargesBreakdown.billed | to_dollar_amount }}</td>
              </tr>
              <tr>
                <td><strong>Total Revenue</strong></td>
                <td class="text-success"><strong>{{ chargesBreakdown.billed - chargesBreakdown.cost | to_dollar_amount }}</strong></td>
              </tr>
            </table>
          </div>
        </div>
        <div class="card mb-2 card-primary">
          <div class="card-header">
            <span class="fa fa-download"></span> To Be Exported
          </div>
          <div class="card-body p-0">
            <table class="table table-bordered table-striped table-hover table-sm mb-0">
              <tr>
                <td><strong>Total Cost</strong></td>
                <td>{{ nonChargesBreakdown.cost | to_dollar_amount }}</td>
              </tr>
              <tr>
                <td><strong>Total to be Invoiced</strong></td>
                <td>{{ nonChargesBreakdown.billed | to_dollar_amount }}</td>
              </tr>
              <tr>
                <td><strong>Total Revenue</strong></td>
                <td class="text-success"><strong>{{ nonChargesBreakdown.billed - nonChargesBreakdown.cost | to_dollar_amount }}</strong></td>
              </tr>
            </table>
          </div>
        </div>
        <vue-form class="mb-2" ref="form" :options="{ permalink: 'processTolls' }">
          <div class="card-header" slot="title">
            <span class="fa fa-paper-plane"></span>
            Charge And Export Results
          </div>
          <template slot="actions">
            <div class="col-sm-12">
              <button type="button" class="btn btn-primary" @click="chargeAndExport()">
                <span class="fa fa-paper-plane fa-fw"></span> Charge & Export
              </button>
            </div>
          </template>
        </vue-form>
      </div>
    </div>
  </div>
</template>

<script>
import EmailInput from '@/components/inputs/EmailInput'
import ResponsiveTable from '@/components/ResponsiveTable'
import VueForm from '@/components/Form'
import Api from '@/vuex/api'
import { OPEN_CONFIRMATION_MODAL } from '@/vuex/types'

export default {
  name: 'TollUpload',
  components: {
    EmailInput,
    ResponsiveTable,
    VueForm
  },
  computed: {
    progressMessage () {
      if (this.progress >= 100) {
        return 'Upload Complete. Processing Tolls...'
      } else {
        return `${this.progress}%`
      }
    },
    reservationsTable () {
      return {
        title: 'Reservations To Be Billed',
        count: this.charges.length,
        columns: [
          { title: 'Res #', span: 1, name: 'reservationId', type: 'text', sort: true },
          { title: 'Toll Description', span: 3, name: 'description', type: 'text', sort: true },
          { title: 'Toll Start', span: 2, name: 'startTime', type: 'date', sort: true },
          { title: 'Toll End', span: 2, name: 'endTime', type: 'date', sort: true },
          { title: 'Toll Fee', span: 2, name: 'cost', type: 'currency', sort: true },
          { title: 'To be Charged', span: 2, name: 'toBeCharged', type: 'currency', sort: true }
        ]
      }
    },
    chargesTable () {
      return {
        title: 'Charges For Invoicing',
        count: this.nonCharges.length,
        columns: [
          { title: 'Car #', span: 1, name: 'carId', type: 'text', sort: true },
          { title: 'Toll Description', span: 3, name: 'description', type: 'text', sort: true },
          { title: 'Toll Start', span: 2, name: 'startTime', type: 'date', sort: true },
          { title: 'Toll End', span: 2, name: 'endTime', type: 'date', sort: true },
          { title: 'Toll Fee', span: 2, name: 'cost', type: 'currency', sort: true },
          { title: 'To be Charged', span: 2, name: 'toBeCharged', type: 'currency', sort: true }
        ]
      }
    }
  },
  data () {
    return {
      reservations: [],
      charges: [],
      hasError: false,
      isUploading: false,
      progress: 0,
      nonCharges: [],
      chargesBreakdown: {
        cost: 0,
        billed: 0
      },
      nonChargesBreakdown: {
        cost: 0,
        billed: 0
      }
    }
  },
  mounted () {
    window.fd.logging = false
    let zone = new window.FileDrop(this.$refs.dropzone, {
      iframe: {
        url: '/api/admin/toll-uploader',
        fileParam: 'tollFile',
        callbackParam: 'iframeCallback'
      }
    })
    let $vm = this
    let token = window.sessionStorage.getItem('token')
    zone.event('send', function (files) {
      $vm.isUploading = true
      files.each(function (file) {
        $vm.hasError = false
        let uploadErrors = []
        if (file.size.length && file.size > 10000000) {
          uploadErrors.push('Filesize is too big')
        }
        if (uploadErrors.length > 0) {
          $vm.hasError = uploadErrors.join('\n')
        } else {
          file.event('xhrSetup', function (xhr, opts) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`)
          })
          file.event('progress', function (sent, total, xhr, event) {
            $vm.progress = parseInt(sent / total * 100, 10)
          })
          file.event('done', function (xhr) {
            $vm.progress = 0
            $vm.isUploading = false
            let json = JSON.parse(xhr.responseText)
            // json is here
            $vm.charges = json.charges
            $vm.nonCharges = json.nonCharges
            $vm.chargesBreakdown = json.chargesBreakdown
            $vm.nonChargesBreakdown = json.nonChargesBreakdown
          })
          file.event('error', function (xhr) {
            uploadErrors.push(xhr.responseText)
            $vm.hasError = uploadErrors.join('\n')
          })
          file.sendTo('/api/admin/toll-uploader', {
            extraHeaders: true,
            method: 'POST'
          })
        }
      })
    })
    zone.event('iframeDone', function (json) {
      $vm.progress = 0
      $vm.isUploading = false
      // json
      $vm.charges = json.charges
      $vm.nonCharges = json.nonCharges
      $vm.chargesBreakdown = json.chargesBreakdown
      $vm.nonChargesBreakdown = json.nonChargesBreakdown
    })
  },
  methods: {
    orderReservations (col) {
      if (col.sortDirection.includes('ASC')) {
        this.charges.sort((a, b) => {
          if (a.suburb) {
            return a[col.name] > b[col.name]
          } else {
            return a[col.name] < b[col.name]
          }
        })
      } else {
        this.charges.sort((a, b) => {
          if (a.suburb) {
            return a[col.name] < b[col.name]
          } else {
            return a[col.name] > b[col.name]
          }
        })
      }
    },
    orderCharges (col) {
      if (col.sortDirection.includes('ASC')) {
        this.nonCharges.sort((a, b) => {
          // sort internal columns (no carId sort)
          if (a.length > 1 && col.name !== 'carId') {
            a.sort((c, d) => {
              return c.toll[col.name] > d.toll[col.name]
            })
          }
          // sort cars
          return a[0].toll.carId > b[0].toll.carId
        })
      } else {
        this.nonCharges.sort((a, b) => {
          // sort internal columns (no carId sort)
          if (a.length > 1 && col.name !== 'carId') {
            a.sort((c, d) => {
              return c.toll[col.name] < d.toll[col.name]
            })
          }
          // sort cars
          if (col.name === 'carId') {
            return a[0].toll.carId < b[0].toll.carId
          } else {
            return a[0].toll.carId > b[0].toll.carId
          }
        })
      }
    },
    async chargeAndExport () {
      let instance = new Api()
      let formData = this.$refs.form.serializeForm()
      formData.charges = this.charges
      formData.nonCharges = this.nonCharges
      await instance.chargeAndExport(formData)
      this.$store.dispatch(OPEN_CONFIRMATION_MODAL, {
        confirmation: 'Successfully charged tolls and sent charges for invoice in an email'
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/admin/TollUpload.vue